/**
 * Maison Montrose — confirmation d'ajout au panier.
 *
 * Horizon n'affiche rien quand un produit est ajouté en AJAX : le tiroir ne
 * s'ouvre que si `auto_open_cart_drawer` est coché, et le compteur du header
 * maison est rendu côté serveur, donc il ne bouge qu'au rechargement complet.
 * Ce module comble les deux trous :
 *
 *   1. un toast de confirmation discret (produit, quantité, prix, jauge de
 *      livraison offerte, CTA vers le tiroir) ;
 *   2. la pastille de comptage du header, mise à jour à chaque mutation du
 *      panier — ajout, modification de quantité, suppression ;
 *   3. l'icône panier du header, qui ouvre le tiroir au lieu de recharger
 *      la page /cart (l'ancre reste le repli sans JS).
 *
 * Le toast se met en retrait si le marchand a activé l'ouverture automatique
 * du tiroir, ou si l'ajout vient du tiroir déjà ouvert : deux retours visuels
 * pour un même geste, c'est du bruit.
 */

import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';

const CART_LINES_UPDATE = 'shopify:cart:lines-update';
const CART_ERROR = 'shopify:cart:error';

const root = document.getElementById('mon-cart-notice');

if (root) init(root);

/**
 * @param {HTMLElement} root
 */
function init(root) {
  const config = {
    enabled: root.dataset.enabled === 'true',
    duration: Math.max(2000, Number(root.dataset.duration) || 6000),
    /** Seuil de livraison offerte, en unités mineures. 0 = désactivé. */
    threshold: Number(root.dataset.threshold) || 0,
    moneyFormat: root.dataset.moneyFormat || '{{amount}}',
    currency: root.dataset.currency || 'EUR',
    cartUrl: root.dataset.cartUrl || '/cart',
    /** Le tiroir s'ouvre déjà tout seul : le toast ferait doublon. */
    drawerAutoOpens: root.dataset.drawerAutoOpen === 'true',
    hasDrawer: root.dataset.hasDrawer === 'true',
    successEyebrow: root.dataset.successEyebrow || 'Rituel mis à jour',
    errorEyebrow: root.dataset.errorEyebrow || 'Ajout au panier',
    errorHeading: root.dataset.errorHeading || 'L’ajout n’a pas abouti.',
    errorFallback: root.dataset.errorFallback || 'Ce soin n’a pas pu être ajouté. Réessayez dans un instant.',
    shippingUnlocked: root.dataset.shippingUnlocked || 'Livraison offerte : c’est débloqué.',
    shippingRemaining: root.dataset.shippingRemaining || 'Plus que [amount] pour la livraison offerte.',
    quantityLabel: root.dataset.quantityLabel || 'Quantité',
  };

  const refs = {
    card: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-card]')),
    image: /** @type {HTMLImageElement} */ (root.querySelector('[data-mon-notice-image]')),
    eyebrow: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-eyebrow]')),
    heading: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-heading]')),
    title: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-title]')),
    meta: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-meta]')),
    price: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-price]')),
    shipping: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-shipping]')),
    shippingText: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-shipping-text]')),
    gauge: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-gauge]')),
    view: /** @type {HTMLButtonElement} */ (root.querySelector('[data-mon-notice-view]')),
    viewCount: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-view-count]')),
    dismiss: Array.from(root.querySelectorAll('[data-mon-notice-dismiss]')),
    timer: /** @type {HTMLElement} */ (root.querySelector('[data-mon-notice-timer]')),
  };

  const defaultHeading = refs.heading?.textContent?.trim() || 'Ajouté à votre panier';

  /** @type {number | undefined} */
  let hideTimeout;

  /** Dernier état connu des lignes du panier : `key` → quantité. */
  let knownLines = new Map();

  /**
   * Dernier total connu du panier, en unités mineures. Rendu par Liquid au
   * chargement, puis rafraîchi à chaque réponse du panier : il sert à estimer
   * la jauge de livraison offerte pendant l'aperçu, avant que le serveur ait
   * répondu.
   */
  let knownTotal = Number(root.dataset.cartTotal) || 0;

  root.style.setProperty('--mon-notice-duration', `${config.duration}ms`);

  // --- Pastille de comptage du header -------------------------------------

  /**
   * @param {number | null | undefined} total
   */
  function renderCount(total) {
    if (typeof total !== 'number' || Number.isNaN(total)) return;

    for (const badge of document.querySelectorAll('[data-mm-cart-count]')) {
      const previous = Number(badge.textContent?.trim()) || 0;
      badge.textContent = total > 99 ? '99+' : String(total);
      badge.toggleAttribute('hidden', total <= 0);

      if (total !== previous && total > 0) {
        // L'ancien code forçait un reflow (`offsetWidth`) pour rejouer
        // l'animation, en pleine frame de mise à jour du panier. Rejouer
        // l'animation existante ne coûte rien au layout ; le repli par
        // attribut passe par une frame, sans lecture de géométrie.
        const animations = /** @type {HTMLElement} */ (badge).getAnimations?.() ?? [];

        if (animations.length > 0) {
          for (const animation of animations) {
            animation.cancel();
            animation.play();
          }
        } else {
          badge.removeAttribute('data-mm-bump');
          requestAnimationFrame(() => badge.setAttribute('data-mm-bump', ''));
        }
      }
    }

    for (const link of document.querySelectorAll('[data-mm-cart-link]')) {
      const label = link.getAttribute('data-mm-cart-label') || 'Panier';
      link.setAttribute('aria-label', total > 0 ? `${label}, ${total}` : label);
    }

    if (refs.viewCount) {
      refs.viewCount.textContent = total > 99 ? '(99+)' : total > 0 ? `(${total})` : '';
    }
  }

  /**
   * Valeur affichée par la pastille, pour pouvoir l'incrémenter sans attendre le
   * serveur puis la restaurer si l'ajout échoue.
   *
   * @returns {number | null} null si elle est illisible (« 99+ ») ou absente.
   */
  function readCount() {
    const badge = document.querySelector('[data-mm-cart-count]');
    if (!badge) return null;
    if (badge.hasAttribute('hidden')) return 0;

    const text = badge.textContent?.trim() ?? '';
    if (text === '') return 0;
    if (text.includes('+')) return null;

    const value = Number(text);
    return Number.isFinite(value) ? value : null;
  }

  /**
   * Quantité du geste en cours, lue dans le formulaire d'origine.
   *
   * @param {EventTarget | null} target
   * @returns {number}
   */
  function readAddedQuantity(target) {
    if (!(target instanceof Element)) return 1;
    const input = /** @type {HTMLInputElement | null} */ (target.querySelector('input[name="quantity"]'));
    const value = Number(input?.value);
    return Number.isFinite(value) && value > 0 ? Math.round(value) : 1;
  }

  // --- Tiroir panier -------------------------------------------------------

  function getDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer) return null;
    // Le module du tiroir est chargé en `fetchpriority="low"` : l'élément peut
    // être dans le DOM sans être encore upgradé.
    customElements.upgrade(drawer);
    return drawer;
  }

  function isDrawerOpen() {
    const drawer = /** @type {any} */ (getDrawer());
    return Boolean(drawer?.isOpen ?? drawer?.hasAttribute('open'));
  }

  /**
   * @returns {boolean} true si le tiroir a pu être ouvert.
   */
  function openDrawer() {
    const drawer = /** @type {any} */ (getDrawer());
    if (typeof drawer?.open !== 'function') return false;
    drawer.open();
    return true;
  }

  // L'icône panier du header maison est une ancre vers /cart. On l'intercepte
  // pour ouvrir le tiroir ; l'ancre reste le repli si le JS ne charge pas ou
  // si le tiroir n'existe pas (template panier, réglage « page »).
  if (config.hasDrawer) {
    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target.closest('[data-mm-cart-link]') : null;
      if (!target) return;
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (!openDrawer()) return;
      event.preventDefault();
      hide();
    });
  }

  // --- Toast ---------------------------------------------------------------

  function restartTimer() {
    clearTimeout(hideTimeout);
    if (!refs.timer) return;

    // Au premier affichage l'animation n'existe pas encore : elle démarrera
    // toute seule avec `data-open`. Aux suivants, on la remet à zéro sans
    // toucher au layout.
    for (const animation of refs.timer.getAnimations?.() ?? []) {
      animation.cancel();
      animation.play();
    }
  }

  refs.timer?.addEventListener('animationend', () => hide());

  // Le compte à rebours est piloté par l'animation CSS de la jauge, mise en
  // pause au survol et au focus. Un garde-fou en JS reste utile si l'animation
  // ne démarre jamais (onglet en arrière-plan, animations désactivées).
  function armFallback() {
    clearTimeout(hideTimeout);
    hideTimeout = window.setTimeout(() => {
      if (root.matches(':hover, :focus-within')) {
        armFallback();
        return;
      }
      hide();
    }, config.duration + 400);
  }

  function positionToast() {
    // Le header maison n'est collant que sur la page d'accueil : quand il a
    // défilé hors de l'écran, le toast remonte dans le coin.
    // `getBoundingClientRect` force un calcul de layout : on l'appelle une seule
    // fois par ouverture, et jamais dans la frame où le panier se re-rend.
    const header = document.querySelector('.mon-premium-header');
    const bottom = header?.getBoundingClientRect().bottom ?? 0;
    root.style.setProperty('--mon-notice-top', `${Math.max(16, Math.round(bottom) + 12)}px`);
  }

  /**
   * @param {'success' | 'error'} state
   */
  function reveal(state, options = {}) {
    root.dataset.state = state;
    if (refs.eyebrow) {
      refs.eyebrow.textContent = state === 'error' ? config.errorEyebrow : config.successEyebrow;
    }
    if (refs.card) {
      refs.card.setAttribute('role', state === 'error' ? 'alert' : 'status');
      refs.card.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
    }
    positionToast();
    root.hidden = false;

    // Un reflow forcé plutôt que deux `requestAnimationFrame` : les frames ne
    // sont pas distribuées dans un onglet masqué, et le toast restait alors
    // « affiché » à `opacity: 0`, sans jamais s'ouvrir ni se refermer. Le coût
    // d'un recalcul de mise en page sur un clic d'ajout est imperceptible ; la
    // fiabilité, elle, ne l'est pas.
    void root.offsetWidth;
    root.dataset.open = 'true';

    if (state === 'success' && !options.pending) {
      restartTimer();
      armFallback();
    } else {
      clearTimeout(hideTimeout);
    }
  }

  /**
   * Filet de l'aperçu : quand la page ne permet pas de lire le titre et le prix
   * (ajout depuis un bloc sans ces informations), on ouvre quand même le toast
   * tout de suite, avec un squelette à la place de la ligne produit. Le geste
   * est ainsi toujours accusé de réception dans la milliseconde, jamais après
   * la réponse du serveur.
   */
  function showPending() {
    if (refs.heading) refs.heading.textContent = defaultHeading;
    root.dataset.pending = 'true';
    reveal('success', { pending: true });
  }

  function hide() {
    clearTimeout(hideTimeout);
    if (root.hidden) return;
    delete root.dataset.open;
    delete root.dataset.pending;

    const done = () => {
      if (!root.dataset.open) root.hidden = true;
    };

    const card = refs.card;
    if (!card) return done();

    let settled = false;
    const onEnd = () => {
      if (settled) return;
      settled = true;
      card.removeEventListener('transitionend', onEnd);
      done();
    };
    card.addEventListener('transitionend', onEnd);
    // Filet : `transitionend` ne se déclenche pas si les transitions sont
    // désactivées (prefers-reduced-motion, onglet caché).
    window.setTimeout(onEnd, 500);
  }

  /**
   * @param {number} minorUnits
   */
  function money(minorUnits) {
    return formatMoney(minorUnits, config.moneyFormat, config.currency);
  }

  /**
   * @param {any} cart - Le panier renvoyé par l'évènement standard.
   */
  function renderShipping(cart) {
    const amount = cart?.cost?.totalAmount?.amount;
    const total = typeof amount === 'string' ? convertMoneyToMinorUnits(amount, config.currency) : null;

    if (total != null) knownTotal = total;

    renderShippingFromTotal(total);
  }

  /**
   * Rend la jauge à partir d'un total de panier déjà connu ou estimé.
   *
   * @param {number | null} total - Total du panier en unités mineures.
   */
  function renderShippingFromTotal(total) {
    if (!refs.shipping || !refs.gauge || !refs.shippingText) return;

    if (!config.threshold || total == null) {
      refs.shipping.hidden = true;
      return;
    }

    const remaining = config.threshold - total;
    const ratio = Math.min(1, Math.max(0, total / config.threshold));

    refs.shipping.hidden = false;
    refs.shipping.dataset.unlocked = remaining <= 0 ? 'true' : 'false';
    refs.shippingText.textContent =
      remaining <= 0 ? config.shippingUnlocked : config.shippingRemaining.replace('[amount]', money(remaining));
    refs.gauge.style.setProperty('--mon-notice-gauge', String(ratio));
  }

  /**
   * `detail.items` porte l'intégralité du panier après l'opération, pas la
   * seule ligne ajoutée, et son ordre n'est pas celui des ajouts. Pour
   * retrouver la ligne concernée on croise deux indices : la quantité qui a
   * augmenté depuis la dernière mutation connue, puis, à défaut,
   * `detail.productId` que le formulaire produit renseigne.
   *
   * @param {any[]} items
   * @param {any} detail
   */
  function findAddedLine(items, detail) {
    const grown = items.find((item) => {
      const previous = knownLines.get(item.key);
      return previous === undefined ? knownLines.size > 0 : item.quantity > previous;
    });
    if (grown) return grown;

    const productId = detail?.productId;
    if (productId != null) {
      const matched = items.find((item) => String(item.product_id) === String(productId));
      if (matched) return matched;
    }

    return items[0];
  }

  /**
   * @param {any[]} items
   */
  function rememberLines(items) {
    knownLines = new Map(items.map((item) => [item.key, item.quantity]));
  }

  /**
   * Chemin d'une image sans son domaine ni ses paramètres (`width`, `v`…),
   * pour reconnaître un même fichier servi en plusieurs tailles.
   *
   * @param {string | null | undefined} url
   * @returns {string}
   */
  function imagePath(url) {
    if (!url) return '';
    let path;
    try {
      path = new URL(url, window.location.href).pathname;
    } catch {
      path = url.split('?')[0];
    }
    // Shopify suffixe d'un identifiant le fichier d'une image copiée sur un
    // autre produit (« vue-7_1a2b3c4d-….png ») : même visuel, on l'ignore.
    return path.replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\.[a-z]+$)/i, '');
  }

  /**
   * @param {any} detail - Le `detail` résolu de l'évènement d'ajout.
   * @param {any} cart
   */
  function showAdded(detail, cart) {
    const items = Array.isArray(detail?.items) ? detail.items : [];
    const item = findAddedLine(items, detail);
    if (!item) return;

    // `item.quantity` est la quantité totale de la ligne après ajout ;
    // `itemCount` porte la quantité du geste en cours.
    const added = Math.max(1, Number(detail?.itemCount) || 1);

    if (refs.heading) refs.heading.textContent = defaultHeading;
    if (refs.eyebrow) refs.eyebrow.textContent = config.successEyebrow;
    if (refs.title) refs.title.textContent = item.product_title || item.title || '';

    if (refs.image) {
      const src = item.featured_image?.url || item.image;
      if (src) {
        // Même largeur (250) que l'image du tiroir : la seconde est servie
        // depuis le cache. Et si l'aperçu montre déjà ce visuel (même
        // fichier, autre taille), on le garde : pas de rechargement visible.
        const next = `${src}${src.includes('?') ? '&' : '?'}width=250`;
        const sameFile = !refs.image.hidden && imagePath(refs.image.currentSrc || refs.image.src) === imagePath(next);
        if (!sameFile) refs.image.src = next;
        refs.image.alt = '';
        refs.image.hidden = false;
      } else {
        refs.image.removeAttribute('src');
        refs.image.hidden = true;
      }
    }

    if (refs.meta) {
      const bits = [];
      if (!item.product_has_only_default_variant && item.variant_title) bits.push(item.variant_title);
      if (added > 1) bits.push(`${config.quantityLabel} ${added}`);
      refs.meta.textContent = bits.join(' · ');
      refs.meta.hidden = bits.length === 0;
    }

    if (refs.price) {
      const unit = Number(item.final_price ?? item.price ?? 0);
      refs.price.textContent = money(unit * added);
    }

    renderShipping(cart);
    delete root.dataset.pending;
    reveal('success');
  }

  /**
   * Convertit un prix tel qu'il est affiché (« €12,80 », « 12.80 € ») en unités
   * mineures. Le dernier séparateur rencontré fait office de virgule décimale,
   * ce qui couvre les deux conventions sans deviner la locale.
   *
   * @param {string} text
   * @returns {number | null} null si la lecture est ambiguë.
   */
  function parseDisplayedPrice(text) {
    const digits = text.replace(/[^\d.,]/g, '');
    if (!digits) return null;

    const lastSeparator = Math.max(digits.lastIndexOf(','), digits.lastIndexOf('.'));
    let normalized;

    if (lastSeparator === -1) {
      normalized = digits;
    } else {
      const units = digits.slice(0, lastSeparator).replace(/[.,]/g, '');
      const decimals = digits.slice(lastSeparator + 1);
      // Trois chiffres après le dernier séparateur : c'était un séparateur de
      // milliers (« 1.200 »), pas une décimale.
      normalized = decimals.length === 3 ? `${units}${decimals}` : `${units}.${decimals}`;
    }

    const value = Number.parseFloat(normalized);
    return Number.isFinite(value) ? Math.round(value * 100) : null;
  }

  /**
   * Le panier répond en ~1 s : d'ici là, rien ne bougeait à l'écran. On affiche
   * tout de suite ce que la page sait déjà du produit — titre, visuel, prix lus
   * dans la section d'origine du clic — puis `showAdded` corrige les mêmes
   * champs avec les données du serveur (quantité réelle, prix de ligne, jauge).
   *
   * Rien n'est deviné : sans titre ni prix trouvés, on ne montre pas d'aperçu et
   * l'ancien comportement s'applique.
   *
   * @param {EventTarget | null} target - L'élément d'où part l'ajout.
   * @param {Array<{merchandiseId?: string | number}>} [lines] - Lignes ajoutées (évènement du formulaire).
   * @returns {boolean} true si un aperçu a été affiché.
   */
  function showPreview(target, lines) {
    if (!(target instanceof Element)) return false;

    // Une seule liste passée à `closest` renverrait l'ancêtre le plus proche —
    // soit le formulaire lui-même, qui ne porte ni titre ni prix. On essaie donc
    // les portées dans l'ordre, de la section maison au conteneur Horizon, et on
    // garde la première qui contient réellement les deux.
    const scopes = ['.mmp', '.mon-carte-produit', '.product-details', '.card-gallery', '.product-card'];
    let titleText = '';
    let priceText = '';
    /** @type {Element | null} */
    let scope = null;

    for (const selector of scopes) {
      const candidate = target.closest(selector);
      if (!candidate) continue;

      const title = candidate.querySelector('.mmp__title, .mon-carte-produit__titre, .product-title, h1, h2, h3');
      // Fiche en mode « programme » (cartes de cure) : le prix courant est
      // celui de la cure cochée (sinon la barre d'achat collante), la fiche
      // n'a pas de `.mmp__price`. Sans ce cas, l'aperçu échouait et le toast
      // montrait un squelette pendant toute l'attente du serveur (~0,9 s).
      const price = candidate.querySelector(
        '.mps__option[aria-checked="true"] .mps__prix strong, .mps__option.is-selected .mps__prix strong, .mmp__price, .mmp__sticky-price, .mon-carte-produit__prix, .price__current, .price'
      );
      let foundTitle = title?.textContent?.trim();
      const foundPrice = price?.textContent?.replace(/\s+/g, ' ').trim();
      const cure = candidate.querySelector('.mps__option[aria-checked="true"] .mps__duree, .mps__option.is-selected .mps__duree');
      const cureLabel = cure?.textContent?.trim();
      if (foundTitle && cureLabel) foundTitle = `${foundTitle} — Cure ${cureLabel}`;

      if (foundTitle && foundPrice) {
        scope = candidate;
        titleText = foundTitle;
        priceText = foundPrice;
        break;
      }
    }

    if (!scope) return false;

    // Duo ou coffret coché dans « Choisissez votre routine » : l'aperçu montre
    // l'offre ajoutée (nom, prix, photo des flacons) et non le soin de la fiche,
    // sinon le visuel changeait sous les yeux à la réponse du serveur.
    const variantId = String(lines?.[0]?.merchandiseId ?? '').split('/').pop();
    const offre = variantId
      ? /** @type {HTMLInputElement | null} */ (scope.querySelector(`[data-offre-input][value="${CSS.escape(variantId)}"]`))
      : null;
    if (offre && offre.dataset.type !== 'seul') {
      if (offre.dataset.offerName) titleText = offre.dataset.offerName;
      if (offre.dataset.priceLabel) priceText = offre.dataset.priceLabel;
    }

    if (refs.heading) refs.heading.textContent = defaultHeading;
    if (refs.eyebrow) refs.eyebrow.textContent = config.successEyebrow;
    if (refs.title) refs.title.textContent = titleText;
    if (refs.price) refs.price.textContent = priceText;
    if (refs.meta) refs.meta.hidden = true;

    if (refs.image) {
      const image = /** @type {HTMLImageElement | null} */ (scope.querySelector('.mmp__gallery img, .mmp__media img, img'));
      const src = (offre && offre.dataset.type !== 'seul' && offre.dataset.image) || image?.currentSrc || image?.src;
      if (src) {
        refs.image.src = src;
        refs.image.alt = '';
        refs.image.hidden = false;
      } else {
        refs.image.removeAttribute('src');
        refs.image.hidden = true;
      }
    }

    // La jauge dépend du total du panier : on l'estime à partir du dernier
    // total connu et du prix affiché, sinon elle apparaîtrait une seconde après
    // le reste du toast. La réponse du serveur la corrige juste après.
    const quantityInput = /** @type {HTMLInputElement | null} */ (scope.querySelector('input[name="quantity"]'));
    const quantity = Math.max(1, Number(quantityInput?.value) || 1);
    const unitPrice = parseDisplayedPrice(priceText);

    renderShippingFromTotal(unitPrice == null ? null : knownTotal + unitPrice * quantity);

    reveal('success');
    return true;
  }

  /**
   * @param {string} message
   */
  function showError(message) {
    if (refs.heading) refs.heading.textContent = config.errorHeading;
    if (refs.title) refs.title.textContent = message || config.errorFallback;
    if (refs.meta) refs.meta.hidden = true;
    if (refs.image) refs.image.hidden = true;
    if (refs.price) refs.price.textContent = '';
    if (refs.shipping) refs.shipping.hidden = true;
    reveal('error');
  }

  // --- Câblage -------------------------------------------------------------

  refs.view?.addEventListener('click', () => {
    if (!openDrawer()) {
      window.location.href = config.cartUrl;
      return;
    }
    hide();
  });

  for (const button of refs.dismiss) {
    button.addEventListener('click', () => hide());
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || root.hidden) return;
    // Un tiroir ou une modale ouverte a la priorité sur le toast.
    if (document.querySelector('dialog[open]')) return;
    hide();
  });

  document.addEventListener(CART_LINES_UPDATE, (event) => {
    const cartEvent = /** @type {any} */ (event);
    const isAdd = cartEvent.action === 'add';
    // Un ajout déclenché depuis le tiroir ouvert se voit déjà dans le tiroir.
    const drawerWasOpen = isDrawerOpen();

    const promise = cartEvent.promise;

    // L'évènement arrive quelques millisecondes après le clic, la promesse ~1 s
    // plus tard : c'est ici, pas dans le `then`, qu'il faut ouvrir le toast.
    const canToast =
      isAdd && config.enabled && !config.drawerAutoOpens && !drawerWasOpen && !isDrawerOpen();
    const previewed = canToast ? showPreview(cartEvent.target, cartEvent.lines) : false;

    // La pastille attendait elle aussi la réponse : le chiffre changeait une à
    // deux secondes après le clic. On l'avance ici, la réponse fait autorité
    // juste après — et la restaure si l'ajout échoue.
    const previousCount = isAdd ? readCount() : null;
    if (previousCount != null) {
      renderCount(previousCount + readAddedQuantity(cartEvent.target));
    }
    if (canToast && !previewed) showPending();

    if (!promise) {
      // Certains chemins ne portent pas de promesse : on retombe sur l'état
      // du panier pour au moins garder la pastille juste.
      fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => renderCount(cart?.item_count))
        .catch(() => {});
      return;
    }

    promise
      .then((/** @type {any} */ result) => {
        const { cart, detail } = result ?? {};
        renderCount(cart?.totalQuantity);

        const items = Array.isArray(detail?.items) ? detail.items : [];
        // Le tiroir a pu s'ouvrir entre l'évènement et la réponse : dans ce
        // cas le toast ferait doublon, l'aperçu est retiré plus bas.
        const shouldToast = canToast && !detail?.didError && !isDrawerOpen();

        if (shouldToast) {
          // Remplace l'aperçu par les données réelles : quantité du geste,
          // prix de ligne, variante et jauge de livraison offerte.
          showAdded(detail, cart);
        } else if (canToast) {
          // L'ajout n'a pas abouti (ou le tiroir s'est ouvert entre-temps) :
          // l'aperçu ne doit pas rester à l'écran. Le message d'erreur, lui,
          // arrive par `shopify:cart:error`.
          hide();
        }

        // Après l'affichage : la comparaison des quantités a besoin de l'état
        // d'avant, et l'instantané doit suivre toutes les mutations, y compris
        // celles qui ne déclenchent pas de toast.
        if (items.length) rememberLines(items);
      })
      .catch(() => {
        if (previewed) hide();
        if (previousCount != null) renderCount(previousCount);
      });
  });

  document.addEventListener(CART_ERROR, (event) => {
    if (!config.enabled) return;
    const detail = /** @type {any} */ (event).detail ?? {};
    const rawMessage = detail.description || detail.errors || detail.message;
    const message = typeof rawMessage === 'string' ? rawMessage.trim() : '';
    const isGeneric = /^(cart error|erreur panier)$/i.test(message);
    showError(message && !isGeneric ? message : config.errorFallback);
  });

  window.addEventListener('pageshow', () => {
    // Retour arrière depuis le bfcache : le panier a pu changer entre-temps.
    if (root.hidden) return;
    hide();
  });
}
